import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { FC, useCallback, useEffect, useState } from "react";
import type { Location } from "react-router-dom";
import { useLocation, useNavigate } from "react-router-dom";

import { appRoutes } from "@/app/router/appRoutes";
import { FullScreenOverlay } from "@/components/full-screen-overlay";
import { IMessageReturnToMessagesModal } from "@/components/modal/imessage-return-to-messages/imessage-return-to-messages-modal";
import { getLearnMoreHrefWithPrice } from "@/components/modal/proactive-upsell/learn-more-href";
import { startProactiveCheckout } from "@/components/modal/proactive-upsell/proactive-checkout";
import { ProactiveTrialPitch } from "@/components/proactive-trial/proactive-trial-pitch";
import { FooterSkipButton } from "@/components/onboarding/components/welcome-wizard";
import { APP_CONFIG } from "@/domains/global/config";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { buildProactivePaywallEventPayload } from "@/domains/metrics/monetization";
import { logger } from "@/modules/logger";
import {
  clearIMessageClaimCheckoutReturnToMessagesPending,
  markIMessageClaimCheckoutReturnToMessagesPending,
  markIMessageClaimCompletionSurfaceExited,
  markIMessageClaimFlowSession,
} from "@/modules/mem-agent/imessage-claim-flow-session";
import {
  getIMessageReturnToMessagesTitle,
  getIMessageReturnToMessagesUrl,
  type IMessageReturnToMessagesScenario,
  type IMessageReturnToMessagesTitle,
} from "@/modules/mem-agent/imessage-return-to-messages";
import { objectModule } from "@/modules/object";
import { toastModule } from "@/modules/toast";
import { windowModule } from "@/modules/window";
import { useAppStore } from "@/store";

const IMESSAGE_CLAIM_PROACTIVE_PAYWALL_SOURCE = "imessage_claim";

const getHomeLocationForCheckoutReturn = ({ location }: { location: Location }): Location => ({
  ...location,
  pathname: appRoutes.notesList({}).path,
  search: "",
  hash: "",
});

export const IMessageOnboardingCompletePage: FC = observer(() => {
  const { store } = useAppStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [returnToMessagesTitle, setReturnToMessagesTitle] =
    useState<IMessageReturnToMessagesTitle | null>(null);
  const isConnected = store.memAgent.isIMessageConnected;
  const proactivePricingAssignment = store.subscriptions.proactivePricingAssignment;
  const proactivePricingError = store.subscriptions.proactivePricingAssignmentError;
  const hasAgentChannelAccess = store.subscriptions.hasAgentChannelAccess;
  const shouldShowProactiveUpsell = isConnected && !hasAgentChannelAccess;
  const proactivePricingIsLoading =
    store.subscriptions.proactivePricingAssignmentIsLoading ||
    (!proactivePricingAssignment && proactivePricingError === undefined);
  const isTrialEligible = store.subscriptions.isProactiveUpsellTrialEligible;
  const proactivePrice = proactivePricingAssignment?.display_price;
  const learnMoreHref = getLearnMoreHrefWithPrice({
    learnMoreHref: APP_CONFIG.URLS.MEM_AGENT_DOCS,
    proactivePriceCents: proactivePricingAssignment?.display_price_cents,
  });

  useEffect(() => {
    markIMessageClaimFlowSession();
  }, []);

  useEffect(() => {
    if (!shouldShowProactiveUpsell) {
      return;
    }

    let canceled = false;

    store.subscriptions
      .fetchProactivePricingAssignment()
      .catch(() => undefined)
      .finally(() => {
        if (!canceled) {
          trackEvent(
            TrackedEvent.PaywallView,
            buildProactivePaywallEventPayload({
              store,
              source: IMESSAGE_CLAIM_PROACTIVE_PAYWALL_SOURCE,
            })
          );
        }
      });

    return () => {
      canceled = true;
    };
  }, [shouldShowProactiveUpsell, store]);

  const navigateHome = useCallback(() => {
    markIMessageClaimCompletionSurfaceExited();
    navigate(appRoutes.notesList({}).path, { replace: true });
  }, [navigate]);

  const showReturnToMessagesModal = useCallback(
    ({ scenario = "mem" }: { scenario?: IMessageReturnToMessagesScenario } = {}) => {
      markIMessageClaimCompletionSurfaceExited();
      setReturnToMessagesTitle(getIMessageReturnToMessagesTitle({ scenario }));
    },
    []
  );

  const showProactiveReturnToMessagesModal = useCallback(() => {
    markIMessageClaimCompletionSurfaceExited();
    setReturnToMessagesTitle(getIMessageReturnToMessagesTitle({ scenario: "proactive" }));
  }, []);

  const closeReturnToMessagesModal = useCallback(() => {
    setReturnToMessagesTitle(null);
    navigateHome();
  }, [navigateHome]);

  const redirectReturnToMessagesModal = useCallback(() => {
    setReturnToMessagesTitle(null);
    navigateHome();
    void windowModule.navigateToExternalAndPreserveHistory({
      url: getIMessageReturnToMessagesUrl(),
    });
  }, [navigateHome]);

  const retryPricingAssignment = useCallback(() => {
    void store.subscriptions.fetchProactivePricingAssignment();
  }, [store.subscriptions]);

  const handleDismiss = useCallback(() => {
    trackEvent(
      TrackedEvent.PaywallCancelClick,
      buildProactivePaywallEventPayload({
        store,
        source: IMESSAGE_CLAIM_PROACTIVE_PAYWALL_SOURCE,
      })
    );
    showReturnToMessagesModal();
  }, [showReturnToMessagesModal, store]);

  const handleSubscribe = useCallback(async () => {
    if (isSubscribing) {
      return;
    }

    if (store.subscriptions.shouldUseAppleProactiveUpgradeModal) {
      trackEvent(
        TrackedEvent.PaywallUpgradeClick,
        buildProactivePaywallEventPayload({
          store,
          source: IMESSAGE_CLAIM_PROACTIVE_PAYWALL_SOURCE,
        })
      );
      markIMessageClaimCompletionSurfaceExited();
      store.subscriptions.openProactiveUpsell();
      navigateHome();
      return;
    }

    trackEvent(
      TrackedEvent.PaywallUpgradeClick,
      buildProactivePaywallEventPayload({
        store,
        source: IMESSAGE_CLAIM_PROACTIVE_PAYWALL_SOURCE,
      })
    );
    markIMessageClaimCompletionSurfaceExited();
    setIsSubscribing(true);
    markIMessageClaimCheckoutReturnToMessagesPending();

    try {
      const checkoutResult = await startProactiveCheckout({
        location: getHomeLocationForCheckoutReturn({ location }),
        store,
      });

      if (!checkoutResult.didStartExternalNavigation) {
        clearIMessageClaimCheckoutReturnToMessagesPending();
        if (checkoutResult.already_paid === true) {
          setIsSubscribing(false);
          showProactiveReturnToMessagesModal();
          return;
        }

        navigateHome();
      }
    } catch (error) {
      clearIMessageClaimCheckoutReturnToMessagesPending();
      trackEvent(TrackedEvent.PaywallUpgradeFailed, {
        ...buildProactivePaywallEventPayload({
          store,
          source: IMESSAGE_CLAIM_PROACTIVE_PAYWALL_SOURCE,
        }),
        failure_stage: "checkout_start",
        failure_reason: "checkout_start_error",
        error_message: error instanceof Error ? error.message : "Unknown checkout error",
      });
      logger.error({
        message: "[IMessageOnboardingCompletePage] Failed to start proactive checkout",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });
      toastModule.triggerToast({
        content:
          error instanceof Error
            ? error.message
            : "Failed to start Proactive checkout. Please try again.",
      });
      setIsSubscribing(false);
    }
  }, [isSubscribing, location, navigateHome, showProactiveReturnToMessagesModal, store]);

  if (!isConnected) {
    return (
      <FullScreenOverlay>
        <Content>
          <Card>
            <Eyebrow>Mem for iMessage</Eyebrow>
            <Title>We couldn’t finish connecting iMessage</Title>
            <Body>Go back to Messages and open your latest Mem link to try again.</Body>
          </Card>
        </Content>
      </FullScreenOverlay>
    );
  }

  if (!shouldShowProactiveUpsell) {
    return (
      <FullScreenOverlay>
        <Content>
          <Card>
            <Eyebrow>Mem for iMessage</Eyebrow>
            <Title>iMessage is connected</Title>
            <Body>Go back to Messages to continue your conversation with Mem Agent.</Body>
            <CompletionFooter>
              <ContinueButton onClick={navigateHome}>Continue to Mem</ContinueButton>
            </CompletionFooter>
          </Card>
        </Content>
      </FullScreenOverlay>
    );
  }

  return (
    <>
      <FullScreenOverlay>
        <Content>
          <UpsellCard>
            <ConnectedTitle>iMessage is connected</ConnectedTitle>
            <ConnectedBody>
              Mem Agent can now reply in Messages. Try Mem Proactive to let it plan, prioritize, and
              remind you before things slip.
            </ConnectedBody>
            <PitchFrame>
              <ProactiveTrialPitch
                heading="Try Mem Proactive"
                isActionLoading={isSubscribing}
                isTrialEligible={isTrialEligible}
                learnMoreHref={learnMoreHref}
                onRetryPricing={retryPricingAssignment}
                onSubscribe={handleSubscribe}
                proactivePrice={proactivePrice}
                pricingError={proactivePricingError}
                pricingIsLoading={proactivePricingIsLoading}
                planFooter={
                  <TrialFooter>
                    <ContinueButton onClick={handleDismiss}>Continue to Mem</ContinueButton>
                  </TrialFooter>
                }
              />
            </PitchFrame>
          </UpsellCard>
        </Content>
      </FullScreenOverlay>
      {returnToMessagesTitle ? (
        <IMessageReturnToMessagesModal
          isOpen
          title={returnToMessagesTitle}
          onClose={closeReturnToMessagesModal}
          onRedirect={redirectReturnToMessagesModal}
        />
      ) : null}
    </>
  );
});

const Content = styled.main`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 72px;
`;

const Card = styled.section`
  width: min(100%, 440px);
  padding: ${({ theme }) => theme.spacing[8]} ${({ theme }) => theme.spacing[6]};
  border-radius: 20px;
  background: ${({ theme }) => theme.colors.surface.modal};
  box-shadow: 0 16px 48px rgb(0 0 0 / 10%);
  text-align: center;
`;

const UpsellCard = styled.section`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: min(100%, 950px);
  height: min(760px, calc(100dvh - 72px));
  min-height: 0;
  padding: ${({ theme }) => theme.spacing[8]} ${({ theme }) => theme.spacing[7]};
  border-radius: 28px;
  background: ${({ theme }) => theme.colors.surface.modal};
  box-shadow: 0 16px 48px rgb(0 0 0 / 10%);

  @media (max-width: 950px) {
    height: min(760px, calc(100dvh - 40px));
    padding: ${({ theme }) => theme.spacing[6]} ${({ theme }) => theme.spacing[5]};
  }
`;

const Eyebrow = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing[3]};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 32px;
  line-height: 1.15;
  letter-spacing: -0.025em;
`;

const ConnectedTitle = styled(Title)`
  text-align: center;
`;

const Body = styled.p`
  margin: ${({ theme }) => theme.spacing[4]} 0 0;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 17px;
  line-height: 1.5;
`;

const ConnectedBody = styled(Body)`
  max-width: 640px;
  margin-inline: auto;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
`;

const PitchFrame = styled.div`
  display: flex;
  min-height: 0;
  margin-top: ${({ theme }) => theme.spacing[7]};
  flex: 1;
`;

const TrialFooter = styled.div`
  display: flex;
  justify-content: center;
  flex-shrink: 0;
`;

const CompletionFooter = styled(TrialFooter)`
  margin-top: ${({ theme }) => theme.spacing[6]};
`;

const ContinueButton = styled(FooterSkipButton)`
  color: ${({ theme }) => theme.colors.text.tertiary};
`;
